<template>
  <MongoDataTable
    ref="table"
    :database="service.database"
    :collection="service.collection"
    :schema="service.schema"
    :insertable="false"
    :editable="false"
    :deletable="false"
    :selectable="false"
    :refreshable="false"
    :filterable="false"
    :columnSelectable="false"
    :sortable="false"
    :rowsPerPage="-1"
    :countable="false"
    :hideFooters="true"
    :onFetch="onTableFetch"
    :onTableItems="v => v"
  >
    <template slot="title">
      <v-icon
        v-html="'mdi-checkbox-blank-circle'"
        :color="getStatusColor(tableStatus)"
        class="mr-3"
      />
      <span v-if="title" class="title d-inline-block my-2">
        {{ title }}
      </span>
      <v-fade-transition>
        <sub v-if="tableErrorsCount > 0" class="caption mx-2 my-3 grey--text">
          <i>({{ tableErrorsCount }} Item Errors)</i>
        </sub>
      </v-fade-transition>
    </template>
    <template slot="headers" slot-scope="props">
      <tr>
        <th v-for="header in table.headers" :key="header.value"
          :class="['column', header.class]"
          role="columnheader"
          scope="col"
        >
          {{ header.text }}
        </th>
      </tr>
    </template>
    <template slot="items" slot-scope="props">
      <tr>
        <td>
          <div class="d-inline-block text-xs-center px-2" style="flex-direction: column">
            <v-icon
              v-html="'mdi-checkbox-blank-circle'"
              small
              :color="getStatusColor(props.item._summary.success)"
            />
          </div>
        </td>
        <td v-for="headerKey in infoHeaders">
          {{ props.item[headerKey] }}
        </td>
        <td v-for="key in types" :key="`${props.index}_${key}`">
          <div class="d-flex align-center" style="flex-direction: column">
            <v-icon
              v-if="props.item.data[key].success"
              v-html="'mdi-check'"
              :color="getStatusColor(props.item.data[key].success)"
            />
            <v-alert v-if="!props.item.data[key].success" color="error" outline :value="true" class="px-3 py-2 my-2">
              {{ props.item.data[key].errors.join('\r\n') }}
            </v-alert>
          </div>
        </td>
      </tr>
    </template>
  </MongoDataTable>
</template>

<script>
import MongoDataTable from '../../components/mongo-datatable/MongoDataTable'
import MongoDataTableItemField from '../../components/mongo-datatable/MongoDataTableItemField'
import MongoConnector from '../../services/mongo-connector.service'
import { getDefaultConnectorSchema } from '../../configs/schemas'
import { titleText } from '../../libs/utils'

export default {
  props: {
    title: { type: String, required: false, default: null },
    infoHeaders: { type: Array, required: false, default: () => [] },
    types: { type: Array, required: false, default: () => [] },
    query: { type: Function, required: false, default: () => {} },
    timeCondition: { type: Object, required: false, default: () => ({}) }
  },
  data () {
    const collection = 'historical-executions'
    const config = getDefaultConnectorSchema(undefined, collection)
    const service = new MongoConnector(config.database, config.collection, config.schema)
    return {
      service,
      ready: false,
      loading: false,
      table: {
        headers: [],
        items: []
      }
    }
  },
  computed: {
    tableStatus () {
      if (!this.ready) return null
      return this.table.items.every(item => item['_summary'].success)
    },
    tableErrorsCount () {
      return this.table.items.filter(item => !item['_summary'].success).length
    }
  },
  async mounted () {
    await this.refresh()
  },
  methods: {
    titleText,
    getStatusColor (value) {
      if (value === null) return null
      return value ? 'success' : 'error'
    },
    setHeaders (items = []) {
      this.table.headers = [
        'status',
        ...this.infoHeaders,
        ...this.types
      ].map(text => ({
        value: text,
        text: titleText(text),
        class: this.infoHeaders.includes(text) || text === 'status' ? 'text-xs-left' : 'text-xs-center'
      }))
    },
    async refresh () {
      this.loading = true
      return this.$refs.table.refreshData().then(() => {
        this.loading = false
        this.ready = true
      })
    },
    async onTableFetch (datatable) {
      const items = await this.query(this.types)
      /**
       * check condition: items `created_at` <= 1 hour
      */
      items.forEach(item => {
        this.types.forEach(key => {
          item.data[key] = this.finalizeItem(item.data, key)
        })
        // find summary
        const summary = {}
        summary.success = Object.keys(item.data).every(key => item.data[key].success)
        summary.errors = Object.keys(item.data).reduce((c, key) => {
          return c.concat(item.data[key].errors)
        }, [])
        item['_summary'] = summary
      })
      this.setHeaders(items)
      this.table.items = items
      return items
    },
    finalizeItem (data, key) {
      let obj = data[key]
      if (obj) {
        obj.success = obj.status !== 'error'
        obj.errors = obj.error ? [obj.error] : []
      } else {
        obj = {}
        obj.success = false
        obj.errors = ['No result']
      }
      return obj
    }
  },
  components: {
    MongoDataTable,
    MongoDataTableItemField
  }
}
</script>
